import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PhotographyApiService } from './api.service';
import { PhotographyListResponse } from '../models/photography-vendor.model';
import { PhotographyRelease } from '../models/photography-catalog.model';

/** Mirrors CreateReleaseRequest in @lcms-photography/core. releaseDate is a calendar date, "YYYY-MM-DD". */
export interface PhotographyReleaseRequest {
  releaseName?: string;
  releaseDate?: string;
}

/** Releases in the lcms-photography catalog. Every product and bundle price belongs to one. */
@Injectable({
  providedIn: 'root',
})
export class PhotographyReleaseService {
  private api = inject(PhotographyApiService);

  list$(): Observable<PhotographyRelease[]> {
    return this.api.get<PhotographyListResponse<PhotographyRelease>>('releases', { limit: 200 }).pipe(
      map((response) => response.items),
    );
  }

  get$(releaseId: string): Observable<PhotographyRelease> {
    return this.api.get<PhotographyRelease>(`releases/${releaseId}`);
  }

  create(release: PhotographyReleaseRequest): Promise<PhotographyRelease> {
    return this.api.create<PhotographyRelease, PhotographyReleaseRequest>('releases', release);
  }

  update(releaseId: string, release: PhotographyReleaseRequest): Promise<PhotographyRelease> {
    return this.api.update<PhotographyRelease, PhotographyReleaseRequest>(`releases/${releaseId}`, release);
  }

  /** Rejects with a 409 while prices still belong to the release */
  delete(releaseId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`releases/${releaseId}`));
  }

  /**
   * The release prices are looked up against today: the latest one whose date isn't in the future,
   * mirroring lcms-photography's pricing lookup. Falls back to the newest release if all are future-dated.
   */
  static current(
    releases: PhotographyRelease[],
    today = new Date().toISOString().slice(0, 10),
  ): PhotographyRelease | undefined {
    const byDateDesc = [...releases].sort((a, b) => (b.releaseDate ?? '').localeCompare(a.releaseDate ?? ''));
    return byDateDesc.find((release) => release.releaseDate && release.releaseDate <= today) ?? byDateDesc[0];
  }
}
